import { ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import isFunction from 'lodash-es/isFunction';
import isUndefined from 'lodash-es/isUndefined';
import { useTranslation } from 'next-i18next';

import { MEDIA_QUERY, Tooltip, useDialog } from '@catawiki/cata-style';

import styles from './BlockContent.module.scss';

export type BlockContentProps = {
  title: string;
  subtitle: ReactNode;
  tooltip?: ReactNode;
};

export const BlockContent = ({
  title,
  subtitle,
  tooltip,
}: BlockContentProps) => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDialog(false);
  const [isMobileView, setIsMobileView] = useState(false);

  const updateIfScreenIsMobile = useCallback(() => {
    if (isFunction(window?.matchMedia)) {
      setIsMobileView(window.matchMedia(MEDIA_QUERY.only.SM).matches);
    }
  }, []);

  useEffect(() => {
    if (isUndefined(window)) return;

    window.addEventListener('resize', updateIfScreenIsMobile);
    // eslint-disable-next-line consistent-return
    return () => {
      if (isUndefined(window)) return;
      window.removeEventListener('resize', updateIfScreenIsMobile);
    };
  }, [updateIfScreenIsMobile]);

  return (
    <Tooltip
      open={isOpen && !!tooltip}
      onToggle={onToggle}
      closeClassName="tw:hidden"
      anchor={
        <div
          className={classNames(
            styles.item,
            'tw:bg-neutral-tertiary tw:text-neutral-primary tw:flex-1',
            'u-p-h-xxl-4 u-p-t-xxl-4 u-p-b-xl',
            'u-m-b-sm-m u-p-h-sm-m u-p-v-sm-m'
          )}
        >
          <h5 className="u-typography-h5">{t(title)}</h5>
          <h6 className="u-typography-h6 u-m-t-m u-color-secondary">
            {subtitle}
          </h6>
        </div>
      }
      shouldFlip
      shouldShift
      placement={isMobileView ? 'bottom' : 'left'}
      toggleOnHover
      toggleOnClick
      withArrow
    >
      {tooltip}
    </Tooltip>
  );
};
